import { jsx as _jsx } from "react/jsx-runtime";
import { Button, SpecraArtifactCard } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { FileText, Presentation, Table } from 'lucide-react';
import { useArtifactDrawer } from '../../../contexts/ArtifactDrawerContext';
import { artifactDataSchema } from '../../../lib/artifacts/types';
import { useChatArtifactStore } from '../../../lib/stores/ChatArtifactStore';
import { ArtifactPreviewInline } from '../../artifacts/ArtifactPreviewInline';
const KIND_ICON = {
    sheet: Table,
    presentation: Presentation
};
const KIND_LABEL = {
    text: 'Text',
    code: 'Code',
    image: 'Image',
    sheet: 'Spreadsheet',
    document: 'Document',
    powerpoint: 'PowerPoint',
    presentation: 'Presentation'
};
/**
 * S7 — in the Workroom an artifact reads as a compact Specra card (kind icon +
 * title + kind) that opens the full editor drawer on click, matching the mockup's
 * card-and-open pattern rather than rendering the heavy inline preview.
 */ function SpecraArtifactCardPart({ artifact }) {
    const { openDrawer } = useArtifactDrawer();
    const selectArtifact = useChatArtifactStore((state)=>state.selectArtifact);
    const Icon = KIND_ICON[artifact.kind] ?? FileText;
    // The drawer renders whichever artifact the store has selected, so select
    // before opening — exactly like every other opener (DocumentInlinePreview,
    // ChatView, ArtifactsView). openDrawer alone would open an empty/stale drawer.
    const open = ()=>{
        selectArtifact(artifact.id);
        openDrawer(artifact.id);
    };
    // The Open action is a real <button> (keyboard-accessible); we intentionally
    // do not make the whole card a mouse-only click target.
    return /*#__PURE__*/ _jsx(SpecraArtifactCard, {
        title: artifact.title,
        description: KIND_LABEL[artifact.kind] ?? artifact.kind,
        icon: /*#__PURE__*/ _jsx(Icon, {
            className: "h-4 w-4"
        }),
        actions: /*#__PURE__*/ _jsx(Button, {
            variant: "ghost",
            size: "sm",
            onClick: open,
            "data-testid": Testids.specraChat.artifactOpen,
            children: "Open"
        }),
        className: "w-full",
        "data-testid": Testids.specraChat.artifact
    });
}
/**
 * Renders an artifact message part with inline preview
 * Artifact registration is handled at the ChatView level when messages are loaded
 */ export function ArtifactPart({ part: { data }, messageId, index, variant = 'default' }) {
    // Parse and validate artifact data using Zod schema
    const parseResult = artifactDataSchema.safeParse(data);
    const artifactData = parseResult.success ? parseResult.data : null;
    if (!artifactData) {
        return null;
    }
    if (variant === 'specra') {
        return /*#__PURE__*/ _jsx("div", {
            className: "w-full",
            children: /*#__PURE__*/ _jsx(SpecraArtifactCardPart, {
                artifact: artifactData
            })
        }, `${messageId}-artifact-${index}`);
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "w-full",
        children: /*#__PURE__*/ _jsx(ArtifactPreviewInline, {
            ...artifactData
        })
    }, `${messageId}-artifact-${index}`);
}
